import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt } from '@fortawesome/sharp-solid-svg-icons';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TiledLink } from '../../../components/styledComponents/tiledLink/TiledLink';
import { convertTime } from '../../../util/helperfunctions';
import { Button } from '../../button/Button';
import { PromoContent } from '../promoContent/PromoContent';
import { SliderTooltip } from '../SliderTooltip';
import { SlideContent } from '../types';
import './ContentItem.css';
import { useContentItem } from './useContentItem';

interface ContentItemProps {
  slideContent: SlideContent;
  promo?: boolean;
  author?: boolean;
}

export function ContentItem({ slideContent, promo = false, author = false }: ContentItemProps) {
  const { t } = useTranslation();
  type Language = 'en' | 'de' | 'sv';

  const { openMediaModal, getTileLink, extractText } = useContentItem(slideContent);

  const [openTooltip, setOpenTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const handleTileTooltip = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenTooltip((prev) => !prev);
  };

  const closeTooltip = () => {
    setOpenTooltip(false);
  };

  // detect click outside of SliderTooltip and close it
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- no-op cleanup, nothing to clean up when the tooltip isn't open
    if (!openTooltip) return () => {};

    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setOpenTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openTooltip]);

  const language = localStorage.getItem('i18nextLng') as Language;

  const rawTitle = extractText(slideContent.title, language || 'en');
  const rawLabel = extractText(slideContent.label, language || 'en');
  const titleText = String(rawTitle).replace('&amp;', '&');

  return (
    <div
      className="content-item-wrapper innerSlide"
      style={{
        backgroundImage: `url(${
          slideContent.chid.startsWith('PB') || author
            ? slideContent.background_image
            : slideContent.thumbnail_image
        })`,
      }}
    >
      <ContentLink
        slideContent={slideContent}
        titleText={titleText}
        getTileLink={getTileLink}
        openMediaModal={openMediaModal}
      />
      {author && (
        <div className="authorPage_featured_info">
          <div className="authorPage_featured_star" />
          <div className="authorPage_featured_text">{t('AT005')}</div>
          <div className="authorPage_featured_play" />
        </div>
      )}
      {slideContent.chid.startsWith('PB') && (
        <PromoContent
          label={rawLabel}
          title={titleText}
          fade={slideContent.fade}
          labelColourFirst={slideContent.labelColourFirst}
          labelColourSecond={slideContent.labelColourSecond}
          subTitle={slideContent?.subTitle}
          url={slideContent?.url}
          company_logo={slideContent?.company_logo}
          company_name={slideContent?.company_name}
        />
      )}
      {slideContent.chid.startsWith('TA') && !author && (
        <span className="ta-badge">
          <FontAwesomeIcon className="ta-shield" icon={faShieldAlt} />
        </span>
      )}
      {!slideContent.chid.startsWith('PB') && (
        <div className="innerFade">
          <div className="innerContent">
            <div className="innerContentContainer">
              <p>{titleText}</p>
              {slideContent?.length && slideContent.length > 0 && (
                <span className="length">
                  {(() => {
                    if (slideContent?.length) return convertTime(slideContent.length);
                    if (slideContent?.days) return `${slideContent.days.length} days`;
                    return '';
                  })()}
                </span>
              )}

              {slideContent?.label && (
                <span
                  className={
                    slideContent?.length && slideContent.length > 0
                      ? 'category'
                      : 'category full_width'
                  }
                >
                  {rawLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {!(promo || slideContent.chid.startsWith('TA') || author) && (
        <>
          <Button
            className="transparent-button ellipsis"
            onClick={handleTileTooltip}
            icon={{ faIconDefinition: faEllipsis }}
            ariaLabel={`${t('SLD001')} ${t('contentItem.options.for')} ${titleText}`}
            variant="ghost"
            iconPosition="center"
            tabIndex={0}
          />
          {openTooltip && (
            <SliderTooltip
              ref={tooltipRef}
              slideContent={slideContent}
              handleTileTooltip={handleTileTooltip}
              closeTooltip={closeTooltip}
            />
          )}
        </>
      )}
    </div>
  );
}

const isExternalPromoLink = (content: SlideContent) => {
  if (!content.chid.startsWith('PB')) return false;

  try {
    return new URL(content?.url).host !== new URL(process.env.REACT_APP_HOST).host;
  } catch (error) {
    return false;
  }
};

const isNotLinkContent = (content: SlideContent) => {
  return content.chid.startsWith('PB') && !content.url && !content.media;
};

interface ContentLinkProps {
  slideContent: SlideContent;
  titleText: string;
  getTileLink: () => string;
  openMediaModal: (e: React.MouseEvent) => void;
}

function ContentLink({ slideContent, titleText, getTileLink, openMediaModal }: ContentLinkProps) {
  const { t } = useTranslation();

  if (isNotLinkContent(slideContent)) {
    return (
      <div className="transparent-button fs-exclude content-container">
        <span className="sr-only">{`${titleText}`}</span>
      </div>
    );
  }
  if (isExternalPromoLink(slideContent)) {
    return (
      <a
        href={slideContent.url}
        target="_blank"
        rel="noopener noreferrer"
        className="transparent-button fs-exclude content-container"
      >
        <span className="sr-only">{`${t('slider.title.play')} ${titleText} ${t('external.link.label.newTab')}`}</span>
      </a>
    );
  }
  return (
    <TiledLink
      to={getTileLink()}
      ariaLabel={`${t('slider.title.play')} ${titleText}`}
      className="transparent-button fs-exclude content-container"
      tabIndex={0}
      onClick={(e) => openMediaModal(e)}
    >
      <span className="sr-only">{t('SLD004')}</span>
    </TiledLink>
  );
}
